import quizCountdownUrl from '@/assets/sounds/01_quiz_countdown.mp3'
import correctUrl from '@/assets/sounds/02_correct_answer_reveal.mp3'
import wrongUrl from '@/assets/sounds/03_wrong_answer.mp3'
import winnerUrl from '@/assets/sounds/04_winner_celebration.mp3'
import roundStartUrl from '@/assets/sounds/05_round_start.mp3'
import timesUpUrl from '@/assets/sounds/06_times_up.mp3'
import playerJoinedUrl from '@/assets/sounds/07_player_joined.mp3'
import selectionUrl from '@/assets/sounds/08_button_selection.mp3'
import pointsUrl from '@/assets/sounds/09_points_awarded.mp3'
import gameOverUrl from '@/assets/sounds/10_game_over.mp3'
import startupUrl from '@/assets/sounds/11_smeegoon_startup.mp3'
import bombCountdownUrl from '@/assets/sounds/12_smeegoon_bomb_countdown_preview.mp3'
import bombExplosionUrl from '@/assets/sounds/13_smeegoon_bomb_explosion_v2_heavy.mp3'
import goSignalUrl from '@/assets/sounds/17_go_signal.mp3'
import goFalseStartUrl from '@/assets/sounds/18_go_false_start.mp3'
import flashbackHideUrl from '@/assets/sounds/19_flashback_hide.mp3'
import bombPassUrl from '@/assets/sounds/20_bomb_pass.mp3'
import nextRoundCountdownUrl from '@/assets/sounds/21_next_round_countdown.mp3'
import gameDrawUrl from '@/assets/sounds/22_game_draw.mp3'
import keepItUpBoostUrl from '@/assets/sounds/23_keep_it_up_boost.mp3'
import keepItUpRescueUrl from '@/assets/sounds/24_keep_it_up_rescue.mp3'

export const SOUNDS = Object.freeze({
  quizCountdown: 'quizCountdown', correct: 'correct', wrong: 'wrong', winner: 'winner',
  roundStart: 'roundStart', timesUp: 'timesUp', playerJoined: 'playerJoined',
  selection: 'selection', points: 'points', gameOver: 'gameOver', startup: 'startup',
  bombCountdown: 'bombCountdown', bombExplosion: 'bombExplosion', goSignal: 'goSignal',
  goFalseStart: 'goFalseStart', flashbackHide: 'flashbackHide', bombPass: 'bombPass',
  nextRoundCountdown: 'nextRoundCountdown', gameDraw: 'gameDraw',
  keepItUpBoost: 'keepItUpBoost', keepItUpRescue: 'keepItUpRescue'
})

const definitions = {
  quizCountdown: { url: quizCountdownUrl, category: 'countdown', volume: .35 },
  correct: { url: correctUrl, category: 'gameplay', volume: .70, duck: true },
  wrong: { url: wrongUrl, category: 'gameplay', volume: .55, duck: true },
  winner: { url: winnerUrl, category: 'celebration', volume: .85, duck: true },
  roundStart: { url: roundStartUrl, category: 'gameplay', volume: .65, duck: true },
  timesUp: { url: timesUpUrl, category: 'gameplay', volume: .65, duck: true },
  playerJoined: { url: playerJoinedUrl, category: 'ui', volume: .55 },
  selection: { url: selectionUrl, category: 'ui', volume: .30 },
  points: { url: pointsUrl, category: 'gameplay', volume: .50 },
  gameOver: { url: gameOverUrl, category: 'celebration', volume: .65, duck: true },
  startup: { url: startupUrl, category: 'ui', volume: .65 },
  bombCountdown: { url: bombCountdownUrl, category: 'countdown', volume: .58 },
  bombExplosion: { url: bombExplosionUrl, category: 'celebration', volume: .90, duck: true },
  goSignal: { url: goSignalUrl, category: 'gameplay', volume: .85, duck: true },
  goFalseStart: { url: goFalseStartUrl, category: 'gameplay', volume: .62 },
  flashbackHide: { url: flashbackHideUrl, category: 'gameplay', volume: .68, duck: true },
  bombPass: { url: bombPassUrl, category: 'gameplay', volume: .55 },
  nextRoundCountdown: { url: nextRoundCountdownUrl, category: 'countdown', volume: .48 },
  gameDraw: { url: gameDrawUrl, category: 'celebration', volume: .65, duck: true },
  keepItUpBoost: { url: keepItUpBoostUrl, category: 'ui', volume: .34 },
  keepItUpRescue: { url: keepItUpRescueUrl, category: 'celebration', volume: .72, duck: true }
}

const categoryVolume = { music: .30, countdown: .70, gameplay: .70, ui: .40, celebration: .80 }
const clamp = value => Math.max(0, Math.min(1, value))

class SoundManager {
  constructor () {
    this.masterVolume = .80
    this.muted = false
    this.active = new Set()
    this.scheduled = new Set()
    this.sequence = 0
    this.maxEffects = 4
    this.blocked = false
    this.context = null
    this.masterGain = null
    this.bufferPromises = new Map()
    this.fallbackAudio = null
  }

  play (name, options = {}) {
    const definition = definitions[name]
    if (!definition || this.muted) return null
    if (options.exclusiveCategory) this.stopCategory(definition.category)
    this.limitConcurrentEffects()
    const record = {
      id: ++this.sequence,
      name,
      category: definition.category,
      volume: options.volume ?? definition.volume,
      stopped: false,
      source: null,
      gain: null,
      audio: null
    }
    this.active.add(record)
    const context = this.getAudioContext()
    record.playPromise = (context
      ? this.playWebAudio(record, definition, options)
      : this.playFallback(record, definition, options)
    ).then(() => {
      this.blocked = false
      return true
    }).catch(error => {
      this.cleanupRecord(record)
      if (error?.name === 'NotAllowedError' || error?.name === 'InvalidStateError') {
        this.blocked = true
        window.dispatchEvent(new CustomEvent('smeegoon-audio-blocked'))
      } else if (error?.name !== 'AbortError') {
        console.warn(`[Sound] Failed to play ${name}`, error?.message || error)
      }
      return false
    })
    return record
  }

  async unlock () {
    this.blocked = false
    const record = this.play(SOUNDS.startup)
    if (!record) return false
    const started = await record.playPromise
    this.blocked = !started
    return started
  }

  schedule (name, delay, options = {}) {
    const timer = setTimeout(() => { this.scheduled.delete(timer); this.play(name, options) }, delay)
    this.scheduled.add(timer)
    return timer
  }

  stop (name) {
    [...this.active].filter(record => record.name === name).forEach(record => this.stopRecord(record))
  }

  stopCategory (category) {
    [...this.active].filter(record => record.category === category).forEach(record => this.stopRecord(record))
  }

  stopAll () {
    this.cancelScheduled()
    ;[...this.active].forEach(record => this.stopRecord(record))
  }

  cancelScheduled () {
    this.scheduled.forEach(timer => clearTimeout(timer))
    this.scheduled.clear()
  }

  setMasterVolume (value) { this.masterVolume = clamp(Number(value) || 0); this.refreshVolumes() }
  setMuted (value) { this.muted = Boolean(value); if (this.muted) this.stopAll() }
  duckMusic () {} // Reserved for the future background-music channel.

  refreshVolumes () {
    if (this.masterGain) this.masterGain.gain.value = this.masterVolume
    this.active.forEach(record => {
      const channelVolume = clamp((categoryVolume[record.category] ?? 1) * record.volume)
      if (record.gain) record.gain.gain.value = channelVolume
      if (record.audio) record.audio.volume = clamp(this.masterVolume * channelVolume)
    })
  }

  getAudioContext () {
    if (this.context) return this.context
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return null
    try {
      this.context = new AudioContextClass()
      this.masterGain = this.context.createGain()
      this.masterGain.gain.value = this.masterVolume
      this.masterGain.connect(this.context.destination)
      return this.context
    } catch (error) {
      console.warn('[Sound] Web Audio unavailable; using single-channel fallback', error?.message || error)
      return null
    }
  }

  async loadBuffer (url) {
    if (!this.bufferPromises.has(url)) {
      const promise = fetch(url)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.arrayBuffer()
        })
        .then(data => this.context.decodeAudioData(data))
        .catch(error => {
          this.bufferPromises.delete(url)
          throw error
        })
      this.bufferPromises.set(url, promise)
    }
    return this.bufferPromises.get(url)
  }

  async playWebAudio (record, definition, options) {
    if (this.context.state !== 'running') await this.context.resume()
    const buffer = await this.loadBuffer(definition.url)
    if (record.stopped) return

    const source = this.context.createBufferSource()
    const gain = this.context.createGain()
    const rate = options.playbackRate ?? (options.durationMs && buffer.duration > 0
      ? (buffer.duration * 1000) / options.durationMs
      : 1)
    source.buffer = buffer
    source.loop = Boolean(options.loop)
    source.playbackRate.value = Math.max(.25, Math.min(4, rate))
    gain.gain.value = clamp((categoryVolume[record.category] ?? 1) * record.volume)
    source.connect(gain)
    gain.connect(this.masterGain)
    record.source = source
    record.gain = gain
    source.onended = () => this.cleanupRecord(record)
    source.start(0)
  }

  async playFallback (record, definition, options) {
    if (typeof Audio === 'undefined') throw new Error('No supported audio playback API')
    // Cast supports one HTML media element. The fallback therefore serializes
    // effects instead of recreating the old multi-element channel pool.
    ;[...this.active].filter(active => active !== record).forEach(active => this.stopRecord(active))
    if (!this.fallbackAudio) this.fallbackAudio = new Audio()
    const audio = this.fallbackAudio
    audio.pause()
    audio.onended = null
    audio.onerror = null
    try { audio.currentTime = 0 } catch (_) { /* Some receivers reject seeks before metadata loads. */ }
    record.audio = audio
    audio.src = definition.url
    audio.preload = 'auto'
    audio.loop = Boolean(options.loop)
    audio.playbackRate = Math.max(.25, Math.min(4, options.playbackRate ?? 1))
    audio.preservesPitch = true
    audio.volume = clamp(this.masterVolume * (categoryVolume[record.category] ?? 1) * record.volume)
    audio.onloadedmetadata = () => {
      if (!options.durationMs || !Number.isFinite(audio.duration) || audio.duration <= 0) return
      if (options.playbackRate == null) audio.playbackRate = Math.max(.25, Math.min(4, (audio.duration * 1000) / options.durationMs))
    }
    audio.onended = () => this.cleanupRecord(record)
    audio.onerror = () => {
      const error = new Error(audio.error?.message || `Media error ${audio.error?.code || ''}`)
      this.cleanupRecord(record)
      console.warn(`[Sound] Failed to load ${record.name}`, error.message)
    }
    await audio.play()
  }

  limitConcurrentEffects () {
    if (this.active.size < this.maxEffects) return
    const oldest = [...this.active].sort((a, b) => a.id - b.id)[0]
    if (oldest) this.stopRecord(oldest)
  }

  cleanupRecord (record) {
    if (record.gain) {
      try { record.gain.disconnect() } catch (_) { /* Already disconnected. */ }
    }
    record.source = null
    record.gain = null
    if (record.audio && record.audio === this.fallbackAudio) {
      record.audio.onended = null
      record.audio.onerror = null
    }
    record.audio = null
    this.active.delete(record)
  }

  stopRecord (record) {
    record.stopped = true
    if (record.source) {
      record.source.onended = null
      try { record.source.stop(0) } catch (_) { /* The source may already have ended. */ }
      try { record.source.disconnect() } catch (_) { /* Already disconnected. */ }
    }
    if (record.audio) {
      record.audio.pause()
      try { record.audio.currentTime = 0 } catch (_) { /* Metadata may not be available yet. */ }
    }
    this.cleanupRecord(record)
  }
}

export const soundManager = new SoundManager()
